import { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { signUp } from 'aws-amplify/auth';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IStepFormProps as Props } from 'interfaces/auth';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { PasswordTextInputControlled } from 'components/_basic/Form/PasswordTextInput';
import { useAuthModalsContext } from 'components/AuthModals';
import { useSetUpPasswordContext } from 'components/SetUpPassword/SetUpPasswordContextProvider/SetUpPasswordContextProvider';

import ToastService from 'helpers/ToastService';
import { enterPasswordValidationSchema } from 'helpers/validations/setUpPasswordFormsValidation';

import styles from '../../SetUpPassword.module.scss';

interface IEnterEmailStepForm {
  password: string;
}

export const EnterEmailStep: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { track, isPending } = usePromiseStatus();

  const { nextStep } = useSetUpPasswordContext();
  const { changeSetUpData, setUpData } = useAuthModalsContext();

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<IEnterEmailStepForm>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver<IEnterEmailStepForm>(enterPasswordValidationSchema),
    defaultValues: {
      password: '',
    },
  });

  const submitHandler: SubmitHandler<IEnterEmailStepForm> = async ({ password }): Promise<void> => {
    try {
      await track(signUp({ username: setUpData.email, password }));
      changeSetUpData((prev) => ({ ...prev, password, type: 'signUp' }));
      nextStep();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'UsernameExistsException') {
          changeSetUpData((prev) => ({ ...prev, password }));
          ToastService.showWarning(t('setUpPasswordWarning'));
          nextStep();
          return;
        }

        if (error instanceof Error) {
          ToastService.showError(error.message);

          if (process.env.NODE_ENV === 'development') {
            console.error(error);
          }
        }
      }
    }
  };

  return (
    <Box className={styles.formContentWrapper}>
      <Grid container alignItems="center">
        <Grid item xs={11}>
          <Typography className={styles.formTitle} variant="h1">
            {t('newPassword')}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <CloseButton onClick={onClose} />
        </Grid>
      </Grid>
      <Box className={styles.formText}>
        <Typography variant="subtitle1">{t('newPasswordFor', { email: setUpData.email })}</Typography>
        <Typography variant="subtitle1">{t('resetPasswordRules')}</Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(submitHandler)} className={styles.formWrapper}>
        <PasswordTextInputControlled name="password" label={t('password')} control={control} />
        <Button fullWidth isLoading={isPending} variant="contained" type="submit" disabled={!isValid}>
          {t('continue')}
        </Button>
      </Box>
    </Box>
  );
};
